import React from 'react';

import { AppealStatus, AppealType } from '@repo/types';

import { UpdateAppealStatusPayload } from '../../types';
import {
  MarkToBeAppealedModal as MarkToBeAppealedModalContent,
  type MarkToBeAppealedSubmitData,
} from '../common/to-be-appealed';

interface MarkToBeAppealedModalProps {
  isOpen: boolean;
  invoiceId: string;
  invoiceNumber: string;
  isLoading: boolean;
  initialAppealType?: AppealType;
  isEditMode?: boolean;
  setLoading: (loading: { toBeAppealed: boolean }) => void;
  onSubmit: (payload: UpdateAppealStatusPayload) => Promise<void>;
  onClose: (success?: boolean, appealType?: AppealType) => void;
}

/**
 * Modal wrapper for the appeals module that transforms the shared submit data
 * into the UpdateAppealStatusPayload format.
 */
export const MarkToBeAppealedModal: React.FC<MarkToBeAppealedModalProps> = ({
  isOpen,
  invoiceId,
  invoiceNumber,
  isLoading,
  initialAppealType,
  isEditMode,
  onSubmit,
  setLoading,
  onClose,
}) => {
  const handleSubmit = async (data: MarkToBeAppealedSubmitData) => {
    const clientRefusedToPay = data.feesCut + data.expensesCut;

    const payload: UpdateAppealStatusPayload = {
      invoiceId,
      nextStatus: AppealStatus.TO_BE_APPEALED,
      appealType: data.appealType,
      remainingAmount: Number(clientRefusedToPay.toFixed(2)),
      comment: data.comment,
      feesCut: data.feesCut,
      expensesCut: data.expensesCut,
      promptPayment: data.promptPayment,
      bulkDiscount: data.bulkDiscount,
      administrativeFee: data.administrativeFee,
      files: data.files,
    };

    if (data.appealType === AppealType.SUBSEQUENT) {
      payload.deadline = data.deadline;
      payload.attyDeadline = data.attyDeadline;
      payload.timekeepers = data.timekeepers;
    } else {
      payload.appealedFees = data.appealedFees;
      payload.appealedExpenses = data.appealedExpenses;
    }

    await onSubmit(payload);
  };

  return (
    <MarkToBeAppealedModalContent
      isOpen={isOpen}
      invoiceId={invoiceId}
      invoiceNumber={invoiceNumber}
      isLoading={isLoading}
      initialAppealType={initialAppealType}
      isEditMode={isEditMode}
      setLoading={setLoading}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
};

export default MarkToBeAppealedModal;
